import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import LeadForm from "@/components/LeadForm";
import NewsletterForm from "@/components/NewsletterForm";
import EligibilityCheck from "@/components/EligibilityCheck";
import FaqList from "@/components/FaqList";
import {
  ShieldCheck,
  BadgeCheck,
  FileCheck2,
  Users,
  GraduationCap,
  Sprout,
  ArrowRight,
  Quote,
} from "lucide-react";

const IMG = {
  hero: "/images/hero.jpg",
  family1: "/images/family1.jpg",
  family2: "/images/family2.jpg",
  consultant1: "/images/consultant1.jpg",
  consultant2: "/images/consultant2.jpg",
  consultation1: "/images/consultation1.jpg",
  consultation2: "/images/consultation2.jpg",
  flag: "/images/mexico-flag.jpg",
};

export default function Home() {
  const { t } = useI18n();

  return (
    <main data-testid="home-page">
      {/* Trust bar */}
      <div className="bg-[#F1F5F9] border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-center gap-x-8 gap-y-1 text-xs sm:text-sm text-slate-600">
          <span className="flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-[#059669]" />
            {t("trust_bar.item1")}
          </span>
          <span className="hidden sm:flex items-center gap-1.5">
            <BadgeCheck size={14} className="text-[#059669]" />
            {t("trust_bar.item2")}
          </span>
          <span className="flex items-center gap-1.5">
            <FileCheck2 size={14} className="text-[#059669]" />
            {t("trust_bar.item3")}
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                <img
                  src={IMG.flag}
                  alt=""
                  className="h-4 w-6 rounded-sm object-cover"
                />
                {t("hero.eyebrow")}
              </span>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
                {t("hero.title")}
              </h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
                {t("hero.subtitle")}
              </p>

              <dl className="mt-8 grid grid-cols-3 gap-4 max-w-xl">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <dt className="text-xs text-slate-500 font-medium">
                      {t(`hero.highlight_${n}_label`)}
                    </dt>
                    <dd className="mt-1 text-xl sm:text-2xl font-bold text-[#0F2942]">
                      {t(`hero.highlight_${n}_number`)}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <img
                    src={IMG.consultant1}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow"
                  />
                  <img
                    src={IMG.consultant2}
                    alt=""
                    className="-ml-4 h-10 w-10 rounded-full object-cover ring-2 ring-white shadow"
                  />
                  <img
                    src={IMG.family2}
                    alt=""
                    className="-ml-4 h-10 w-10 rounded-full object-cover ring-2 ring-white shadow"
                  />
                </div>
                <span className="text-slate-700">
                  <span className="font-semibold">
                    {t("hero.highlight_1_number")}
                  </span>{" "}
                  {t("hero.highlight_1_label")}
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* Social support */}
      <section id="apoyo" className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-widest text-[#059669]">
                {t("apoyo.eyebrow")}
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                {t("apoyo.title")}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t("apoyo.subtitle")}
              </p>
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                <img
                  src={IMG.family1}
                  alt=""
                  className="h-72 w-full object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 grid gap-5">
              {[
                { n: 1, Icon: Users },
                { n: 2, Icon: GraduationCap },
                { n: 3, Icon: Sprout },
              ].map(({ n, Icon }) => (
                <div
                  key={n}
                  data-testid={`apoyo-card-${n}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 flex gap-5 items-start"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[#059669]">
                    <Icon size={22} />
                  </span>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
                        {t(`apoyo.card${n}_title`)}
                      </h3>
                      <span className="shrink-0 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                        {t(`apoyo.card${n}_amount`)}
                      </span>
                    </div>
                    <p className="mt-2 text-slate-600 leading-relaxed">
                      {t(`apoyo.card${n}_desc`)}
                    </p>
                  </div>
                </div>
              ))}
              <a
                href="#newsletter"
                data-testid="apoyo-cta"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#047857] hover:text-[#059669]"
              >
                {t("apoyo.cta")} <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Financial literacy */}
      <section id="educacion" className="bg-[#0F2942] text-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">
                {t("educacion.eyebrow")}
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                {t("educacion.title")}
              </h2>
              <p className="mt-4 text-white/70 leading-relaxed">
                {t("educacion.subtitle")}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <img
                  src={IMG.consultation1}
                  alt=""
                  className="h-40 w-full rounded-xl object-cover"
                />
                <img
                  src={IMG.consultation2}
                  alt=""
                  className="h-40 w-full rounded-xl object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  data-testid={`step-${n}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                >
                  <span className="text-emerald-300 font-mono text-sm font-semibold">
                    {t(`educacion.step${n}_num`)}
                  </span>
                  <h3 className="mt-1 text-lg font-semibold text-white">
                    {t(`educacion.step${n}_title`)}
                  </h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    {t(`educacion.step${n}_desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Consultation */}
      <section id="asesoria" className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 items-center">
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <img
                  src={IMG.consultant1}
                  alt=""
                  className="h-[480px] w-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-widest text-[#059669]">
                {t("asesoria.eyebrow")}
              </p>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                {t("asesoria.title")}
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t("asesoria.subtitle")}
              </p>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6">
                <div className="flex items-center gap-4">
                  <img
                    src={IMG.consultant2}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">
                      {t("asesoria.expert_name")}
                    </p>
                    <p className="text-sm text-slate-500">
                      {t("asesoria.expert_role")}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed">
                  {t("asesoria.expert_bio")}
                </p>
                <div className="mt-4 flex items-start gap-3 text-slate-700 italic">
                  <Quote size={18} className="mt-1 shrink-0 text-[#059669]" />
                  <p>{t("asesoria.expert_quote")}</p>
                </div>
              </div>

              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {[1, 2, 3, 4].map((n) => (
                  <li
                    key={n}
                    data-testid={`bullet-${n}`}
                    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <BadgeCheck className="mt-0.5 shrink-0 text-[#059669]" size={20} />
                    <div>
                      <p className="font-semibold text-slate-900">
                        {t(`asesoria.bullet${n}_title`)}
                      </p>
                      <p className="text-sm text-slate-600">
                        {t(`asesoria.bullet${n}_desc`)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility widget */}
      <section className="bg-[#F8FAFC] py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <EligibilityCheck />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-[#059669]">
            {t("faq.eyebrow")}
          </p>
          <h2 className="mt-3 text-center font-serif text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            {t("faq.title")}
          </h2>
          <div className="mt-10">
            <FaqList />
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="bg-[#F8FAFC] py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </section>
    </main>
  );
}
